import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import type { Location } from 'react-router-dom'
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { makeGetShortlistsSelectedItems } from '@disco/state-management/selectors/selection'
import { makeGetShortlistTracks } from '@disco/state-management/selectors/shortlists'

import type { Track } from 'types/track'

import { getShortlistBaseRoute } from 'utils/routing'

import BulkEditor from 'components/bulk-editor'

const makeMapStateToProps = (state, props) => {
  const getShortlistsSelectedItems = makeGetShortlistsSelectedItems()
  const getShortlistTracks = makeGetShortlistTracks()
  const isEditAll = props.searchParams.get('all')
  const items = isEditAll
    ? getShortlistTracks(state, props.params)
    : getShortlistsSelectedItems(state)

  return {
    selectedItems: items.filter(track => !track.outside_your_territories),
    params: props.params,
    isEditAll,
  }
}

const mapDispatchToProps = {
  sendMixpanelEvent,
}

const connector = connect(makeMapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & {
  navigate: NavigateFunction
  location: Location
}

class ShortlistBulkEdit extends React.Component<Props> {
  // Capture initial items on mount and preserve them for the component's lifetime.
  // This prevents the bulk edit modal from closing when navigating to/from nested modals
  private _initialItems: Track[] = this.props.selectedItems

  _onClose = () => {
    const baseRoute = getShortlistBaseRoute(this.props.location)
    this.props.navigate(baseRoute || '/')
  }

  componentDidMount() {
    if (this._initialItems.length === 0) {
      const baseRoute = getShortlistBaseRoute(this.props.location)
      this.props.navigate(baseRoute || '/')
    }

    if (this.props.isEditAll) {
      this.props.sendMixpanelEvent('Open Edit all in shortlist')
    }
  }

  render() {
    if (this._initialItems.length === 0) return null
    return (
      <BulkEditor
        editing={{
          tracks: this._initialItems,
        }}
        onRequestClose={this._onClose}
      />
    )
  }
}

const ConnectedShortlistBulkEdit = connector(ShortlistBulkEdit)

export default function ShortlistBulkEditWithRouter(props) {
  const navigate = useNavigate()
  const params = useParams<{ shortlistId: string }>()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  return (
    <ConnectedShortlistBulkEdit
      {...props}
      navigate={navigate}
      params={params}
      searchParams={searchParams}
      location={location}
    />
  )
}
